import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ButtonBase, Typography } from "@mui/material";
import { type ReactNode, useState } from "react";

import { CRM_COLORS } from "../../lib/crm";

type CrmSectionProps = {
  id: string;
  title: ReactNode;
  children: ReactNode;
  testId?: string;
};

// A collapsible group of a record page, as Salesforce shows detail sections.
export const CrmSection = ({ id, title, children, testId }: CrmSectionProps) => {
  const [open, setOpen] = useState(true);
  return (
    <Box component="section" data-testid={testId} sx={{ mb: "12px" }}>
      <ButtonBase
        onClick={() => setOpen(current => !current)}
        aria-expanded={open}
        aria-controls={id}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          gap: "6px",
          px: "8px",
          py: "6px",
          borderRadius: "4px",
          backgroundColor: CRM_COLORS.pageBackground,
          fontSize: "14px",
          fontWeight: 700,
          color: CRM_COLORS.text,
        }}
      >
        <ExpandMoreIcon
          fontSize="small"
          sx={{ transform: open ? "none" : "rotate(-90deg)", transition: "transform 120ms" }}
        />
        {title}
      </ButtonBase>
      <Box id={id} hidden={!open}>
        {children}
      </Box>
    </Box>
  );
};

export type CrmFieldItem = {
  key: string;
  label: ReactNode;
  value: ReactNode;
  valueAriaLabel?: string;
  gridColumn?: string | { xs: string; md: string };
  testId?: string;
};

// Labelled values in two columns, as Salesforce lists a record's fields.
export const CrmFieldList = ({ items }: { items: CrmFieldItem[] }) => (
  <Box
    component="dl"
    sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: "32px", m: 0, px: "16px" }}
  >
    {items.map(item => (
      <Box
        key={item.key}
        data-testid={item.testId}
        sx={{ py: "8px", borderBottom: `1px solid ${CRM_COLORS.cardBorder}`, minWidth: 0, gridColumn: item.gridColumn }}
      >
        <Typography component="dt" sx={{ fontSize: "12px", color: CRM_COLORS.label, lineHeight: 1.5 }}>
          {item.label}
        </Typography>
        <Box
          component="dd"
          aria-label={item.valueAriaLabel}
          sx={{ m: 0, minHeight: "20px", fontSize: "14px", color: CRM_COLORS.text, overflowWrap: "anywhere" }}
        >
          {item.value}
        </Box>
      </Box>
    ))}
  </Box>
);
